import React, { useState, useEffect } from 'react';
import { useNotifications } from '../contexts/NotificationContext';
import GoogleAdsDashboard from './GoogleAdsDashboard';

const GoogleAdsSection = ({ currentUser, permissions }) => {
  const { showNotification } = useNotifications();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      const userCells = currentUser?.cells?.join(',') || '';
      const response = await fetch(`/api/google-ads/accounts?userCells=${encodeURIComponent(userCells)}`);
      
      if (!response.ok) {
        throw new Error('Error al cargar cuentas de Google Ads');
      }
      
      const data = await response.json();
      setAccounts(data);
    } catch (error) {
      console.error('Error cargando cuentas:', error);
      showNotification('Error al cargar cuentas de Google Ads', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAccountUpdate = () => {
    loadAccounts();
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'accounts', label: 'Cuentas', icon: '🔗' },
    { id: 'alerts', label: 'Alertas', icon: '🔔' },
    { id: 'reports', label: 'Reportes', icon: '📈' }
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              🎯 Google Ads - Monitoreo y Alertas
            </h1>
            <p className="text-gray-600 mt-1">
              Sistema completo de monitoreo en tiempo real para múltiples cuentas de Google Ads
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-500">
              {accounts.length} cuenta{accounts.length !== 1 ? 's' : ''} configurada{accounts.length !== 1 ? 's' : ''}
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm text-green-600 font-medium">En línea</span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'dashboard' && (
            <GoogleAdsDashboard 
              accounts={accounts}
              onRefresh={loadAccounts}
            />
          )}
          
          {activeTab === 'accounts' && (
            <GoogleAdsAccountsManager 
              accounts={accounts}
              onAccountUpdate={handleAccountUpdate}
              currentUser={currentUser}
              permissions={permissions}
            />
          )}
          
          {activeTab === 'alerts' && (
            <GoogleAdsAlertsManager 
              accounts={accounts}
              onRefresh={loadAccounts}
              currentUser={currentUser}
              permissions={permissions}
            />
          )}
          
          {activeTab === 'reports' && (
            <GoogleAdsReports 
              accounts={accounts}
              currentUser={currentUser}
              permissions={permissions}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Componente temporal para gestión de cuentas
const GoogleAdsAccountsManager = ({ accounts, onAccountUpdate, currentUser, permissions }) => {
  const { showNotification } = useNotifications();
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAccount, setNewAccount] = useState({
    account_name: '',
    customer_id: '',
    cell: currentUser?.cells?.[0] || ''
  });

  const handleAddAccount = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/google-ads/accounts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...newAccount,
          created_by: currentUser.id
        })
      });

      if (!response.ok) {
        throw new Error('Error al agregar cuenta');
      }

      showNotification('Cuenta agregada exitosamente', 'success');
      setShowAddForm(false);
      setNewAccount({ account_name: '', customer_id: '', cell: currentUser?.cells?.[0] || '' });
      onAccountUpdate();
    } catch (error) {
      console.error('Error:', error);
      showNotification('Error al agregar cuenta', 'error');
    }
  };

  const handleDeleteAccount = async (accountId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta cuenta?')) return;

    try {
      const response = await fetch(`/api/google-ads/accounts/${accountId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Error al eliminar cuenta');
      }

      showNotification('Cuenta eliminada exitosamente', 'success');
      onAccountUpdate();
    } catch (error) {
      console.error('Error:', error);
      showNotification('Error al eliminar cuenta', 'error');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">Gestión de Cuentas Google Ads</h3>
        {permissions.canManageGoogleAdsAccounts && (
          <button
            onClick={() => setShowAddForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            + Agregar Cuenta
          </button>
        )}
      </div>

      {showAddForm && (
        <div className="bg-gray-50 p-6 rounded-lg">
          <h4 className="text-md font-medium text-gray-900 mb-4">Nueva Cuenta Google Ads</h4>
          <form onSubmit={handleAddAccount} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Nombre de la Cuenta</label>
              <input
                type="text"
                value={newAccount.account_name}
                onChange={(e) => setNewAccount({...newAccount, account_name: e.target.value})}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Customer ID</label>
              <input
                type="text"
                value={newAccount.customer_id}
                onChange={(e) => setNewAccount({...newAccount, customer_id: e.target.value})}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="123-456-7890"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Célula</label>
              <input
                type="text"
                value={newAccount.cell}
                onChange={(e) => setNewAccount({...newAccount, cell: e.target.value})}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div className="flex space-x-3">
              <button
                type="submit"
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                Guardar
              </button>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cuenta
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Célula
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {accounts.map((account) => (
                <tr key={account.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{account.account_name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{account.customer_id}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{account.cell}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      account.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {account.is_active ? 'Activa' : 'Inactiva'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {permissions.canDeleteGoogleAds && (
                      <button
                        onClick={() => handleDeleteAccount(account.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Eliminar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Componente temporal para gestión de alertas
const GoogleAdsAlertsManager = ({ accounts, currentUser, permissions }) => {
  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900">Gestión de Alertas</h3>
      <div className="bg-gray-50 p-8 rounded-lg text-center">
        <div className="text-4xl mb-4">🔔</div>
        <h4 className="text-lg font-medium text-gray-900 mb-2">Sistema de Alertas</h4>
        <p className="text-gray-600">
          Configura alertas personalizadas para monitorear tus métricas de Google Ads en tiempo real.
        </p>
        <div className="mt-4 text-sm text-gray-500">
          Próximamente: Configuración de umbrales para presupuesto, CPC, CTR, conversiones y ROAS.
        </div>
      </div>
    </div>
  );
};

// Componente temporal para reportes
const GoogleAdsReports = ({ accounts, currentUser, permissions }) => {
  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900">Reportes y Exportación</h3>
      <div className="bg-gray-50 p-8 rounded-lg text-center">
        <div className="text-4xl mb-4">📈</div>
        <h4 className="text-lg font-medium text-gray-900 mb-2">Reportes Personalizados</h4>
        <p className="text-gray-600">
          Genera y exporta reportes detallados de rendimiento de tus campañas de Google Ads.
        </p>
        <div className="mt-4 text-sm text-gray-500">
          Próximamente: Exportación en PDF, Excel y CSV con filtros personalizables.
        </div>
      </div>
    </div>
  );
};

export default GoogleAdsSection;
